<template>
    <div class="matches__container active">
        <div class="matches">
            <div class="recommendations">
                <div class="section-title">{{$t("recommendations_plural")}}</div>
                <div class="recommendations__cards">
                    <MatchCard
                        v-for="(match, index) in normalAlternatives"
                        :key="index"
                        :match="match"
                        :hiddenFields="hiddenFields"
                        :showMore="showMore"
                        :ledAlternative=false
                        :clickable="false"
                        :selected="isCardSelected(match.product_code)"
                        @toggleShowMore="toggleShowMore"
                        @clicked="onClickCard"/>
                    <MatchCard
                        v-for="(match, index) in ledAlternatives"
                        :key="'led'+index"
                        :match="match"
                        :hiddenFields="hiddenFields"
                        :showMore="showMore"
                        :ledAlternative=true
                        :clickable="false"
                        @toggleShowMore="toggleShowMore"
                        @clicked="onClickCard"/>
                    <div class="no-result" v-if="result.matches.length === 0">
                        <p>{{ $t(noAlternativesMessage) }}</p>
                    </div>
                </div>
                <div v-if="showCompareButton" @click="$store.dispatch('search/openCompare')" class="compare-btn btn btn-primary btn-block">
                    Compare!
                </div>
            </div>
            <div class="selection">
                <div class="section-title">{{$t("your_product")}}</div>
                <MatchCard
                    :type="'result'"
                    :match="result.result"
                    :hiddenFields="hiddenFields"
                    :showMore="showMore"
                    :clickable="false"
                    @toggleShowMore="toggleShowMore"/>
                
                <div class="selection-info">
                    {{ $t("ballast_notice") }}<br />
                    <a href="https://www.assets.signify.com/is/content/Signify/phl-ledtube-eu-compatibility-list-professional" target="_blank" rel="noopener noreferrer">
                        {{ $t("ballast_notice_tled_link_text") }}
                    </a><br />
                    <a href="https://www.assets.signify.com/is/content/Signify/phl-led-pl-eu-compatibility-list-professional" target="_blank" rel="noopener noreferrer">
                        {{ $t("ballast_notice_pl_led_link_text") }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { countBy, isEqual } from 'lodash';
import { http } from "@/services/http";
import { isFalsy, hiddenFieldPr, isLed, arrAnd } from '@/services/utils';
import MatchCard from "@/components/MatchCard";

export default {
    name: 'matches',

    data() {
        return {
            showMore: false,
        }
    },

    props: {
        result: Object,
    },

    components: {
        MatchCard
    },

    computed: {
        filteredAlternatives() {
            const matches = this.result.matches.map(e => e.match);
            const selectedFilters = this.$store.state.search.matchesFilters;

            if (this.isEmptyFilter(selectedFilters)) {
                return matches;
            }

            // Group each property so we can OR values from same properties, but
            // AND different properties together
            const filteredMatches = [];
            for (let property in selectedFilters) {
                if (selectedFilters[property].length == 0)
                    continue;
                let propertyMatches = [];
                filteredMatches.push(propertyMatches);

                for (let match of matches) {
                    if (propertyMatches.indexOf(match) !== -1)
                        continue;
                    for (let value of selectedFilters[property]) {
                        if (match.hasOwnProperty(property) && match[property] === value) {
                            propertyMatches.push(match);
                            break;
                        }
                    }
                }
            }

            // Get the intersection of all the filtered match groups:
            let finalFilteredMatches = filteredMatches[0];
            for (let i = 1; i < filteredMatches.length; ++i) {
                finalFilteredMatches = finalFilteredMatches.filter(v => filteredMatches[i].includes(v));
            }

            return finalFilteredMatches;
        },

        showCompareButton() {
            return this.$store.state.search.compareTargets.length > 1;
        },

        noAlternativesMessage() {
            if (this.$store.state.search.alternative == "led")
                return 'led_no_recommendations'
            return 'results_no_recommendations';
        },

        // If we're in LED alternatives mode, every alternative will be
        // normal
        normalAlternatives() {
            if (this.$store.state.search.alternative == "led") {
                return this.filteredAlternatives;
            }

            return this.result.matches.map(e => e.match)
                .filter(m => (m["lamp_type"] != "LED" && 
                    m["driver_type"] != "LED" &&
                    m["dr_type"] != "LED"));

        },

        ledAlternatives() {
            if (this.$store.state.search.alternative == "led")
                return [];

            return this.result.matches.map(e => e.match)
                .filter(m => (m["lamp_type"] == "LED" ||
                    m["driver_type"] == "LED" ||
                    m["dr_type"] == "LED"));
        },

        hiddenFields() {
            const result = this.result.result
            const matches = this.result.matches;

            // Decide which fields are subjected to hidding
            // Only fields which are displayed in all cards can be hidden
            let fieldMask = Object.keys(result);
            for (let {match} of matches) 
            {
                fieldMask = arrAnd(fieldMask,
                    this.$store.state.search.cardProperties
                        [isLed(match) ? "led" : "conventional"])
            }


            // Don't hide anything if there aren't any matches to compare to
            if (matches.length == 0)
                return [];

            // Start with the whole set, and remove fields as we find 
            // differences
            let fieldList = Object.keys(result);
            for (let field in result)
            {
                for (let {match} of matches)
                {
                    if (match[field] === null || match[field] === "undefined" ||
                        !isEqual(match[field], result[field]))
                    {
                        fieldList.splice(fieldList.indexOf(field), 1);
                        break;
                    }
                }  
            }
            // Apply mask for filters that can be hidden
            fieldList = arrAnd(fieldList, fieldMask);

            return fieldList;
        },

        resultMatchesMetaInfo() {
            const result = this.result.result;
            const matches = this.result.matches;

            let toReturn = {
                commonEmptyProps: [],
                resultDifferences: []
            };

            //for each of the matches, check which props differ from the result.
            matches.map((match, index) => {
                toReturn['match' + index + 'Differences'] = [];
                for (let matchProp in match.match) {
                    //isEqual for deep comparison and arrays and such
                    if(!isEqual(match.match[matchProp], result[matchProp])) { 
                        toReturn['match' + index + 'Differences'].push(matchProp);
                    }
                }
            });

            //make a list of the differing props in the result.
            matches.map((match, index) => {
                toReturn.resultDifferences.push(...toReturn['match' + index + 'Differences']);
            });

            //filter duplicates
            toReturn.resultDifferences = [...new Set(toReturn.resultDifferences)];

            //list common props that are empty, which tells the card to not show these props
            const emptyProps = [];
            matches.map((match, index) => {
                for (let matchProp in match.match) {
                    if(isFalsy(match.match[matchProp])) {
                        emptyProps.push(matchProp);
                    }
                }
            });
            for(let resultProp in result) {
                if(isFalsy(result[resultProp])) {
                    emptyProps.push(resultProp);
                }
            }
            const emptyPropOccurrences = countBy(emptyProps);
            Object.keys(emptyPropOccurrences).map(prop => {
                if(emptyPropOccurrences[prop] === matches.length + 1) {
                    toReturn.commonEmptyProps.push(prop);
                }
            })

            return toReturn;
        }
    },

    

    methods: {
        onClickCard(product_code) {
            //this.$store.dispatch("search/toggleCompareSelection", product_code);
        },

        isEmptyFilter(filters) {
            if (!filters)
                return true;
            for (let filter in filters) {
                if (filters[filter].length > 0)
                    return false;
            }
            return true;
        },

        exportResult(pk) {
            this.$emit('export', pk);
        },

        toggleShowMore() {
            this.showMore = !this.showMore;
        },

        isCardSelected(product_code) {
            return this.$store.state.search.compareTargets.indexOf(product_code) !== -1;
        }
    }
}
</script>


<style lang="scss" scoped >
    .compare-btn {
        max-width: 220px;
    }

    .selection-info {
        margin-top: 1em;
        max-width: 220px;
    }

    .section-title {
        font-weight: bold;
        font-size: $nc-fsz-l;
        margin-bottom: 20px;
    }

    .no-result {
        // padding: 62px 62px 62px 15px;
        padding: 15px;
        align-self: center;
        max-width: 530px;


        + .match {
            margin-left: auto;
        }
    }

    .matches__container:not(.active) {
        opacity: 0;
        z-index: -1;
        display: none;
    }

    .matches {
        display: flex;

        @include media-breakpoint-down(sm) {
            flex-direction: column;
        }

        .recommendations {
            &__cards {
                display: flex;
                flex-wrap: wrap;
            }

            margin-right: 10px;
            margin-bottom: 10px;
            .match {
                margin-right: 10px;
                margin-bottom: 10px;

                @include media-breakpoint-down(md) {
                    margin-right: 0px;
                }
            }
        }

        .selection {
            margin-bottom: 20px;
        }
        

        &__loading {
            font-weight: bold;
            padding: 50px 50px 50px 0;
        }

        &__heading {
            display: flex;
            justify-content: space-between;

            h4 {
                margin-bottom: 0;
            }

            .export-buttons {
                display: flex;
            }

            button {
                display: flex;
                align-items: center;
                align-self: center;
                border: 0;
                background-color: transparent;
                font-size: $tiny-font-size;
                color: $medium-blue;
                padding: 0 0 0 20px;

                &:active,
                &:focus,
                &:active {
                    outline: none;
                    color: $dark-blue;
                }

                &::before {
                    content: "";
                    display: inline-block;
                    background: url("../assets/svg/export.svg") no-repeat;
                    width: 20px;
                    height: 20px;
                    margin-right: 5px;
                }
            }
        }
    }
</style>
